import React, { useState } from 'react'
import {
  CCard,
  CCardBody,
  CForm,
  CFormFeedback,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
} from '@coreui/react-pro'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent'
import { extensionValues, formatValue } from './valueFormat'

// Shared body of the minimum and maximum limit cards. `field` is the payload key the API
// reads (min_limit / max_limit) and `limitType` the limit_type it expects alongside it.
const LimitCard = ({
  field,
  limitType,
  title,
  note,
  current,
  edge,
  options,
  symbol,
  requiredMessage,
  fetchData,
}) => {
  const [loader, setLoader] = useState(false)
  const formik = useFormik({
    // The API can hand back "5.00"; normalise so it matches an option's value.
    initialValues: { [field]: String(Number(current) || 0) },
    enableReinitialize: true,
    validationSchema: Yup.object().shape({ [field]: Yup.string().required(requiredMessage) }),
    onSubmit: (data) => {
      setLoader(true)
      data.limit_type = limitType
      CommonService.updateSetFantasyValueLimit(data)
        .then((res) => {
          if (res.status === 200) {
            ToastComponent(res.message, 'success')
            fetchData()
          } else {
            ToastComponent(res.message, 'error')
          }
        })
        .catch(() => ToastComponent('Could not save the limit', 'error'))
        .finally(() => setLoader(false))
    },
  })

  const selected = Number(formik.values[field]) || 0
  const added = extensionValues(edge, selected)
  const invalid = formik.errors[field] && formik.touched[field]

  return (
    <CCard className="fv-card h-100">
      <CCardBody className="d-flex flex-column">
        <h2 className="fv-card-title">{title}</h2>
        <p className="fv-card-note">{note}</p>

        <CForm onSubmit={formik.handleSubmit} className="d-flex flex-column flex-grow-1">
          <CFormLabel htmlFor={field} className="fw-semibold">
            Limit
          </CFormLabel>
          <CFormSelect
            id={field}
            name={field}
            invalid={!!invalid}
            value={formik.values[field]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            <option value="0">No extension</option>
            {options.map((value) => (
              <option key={value} value={value}>
                {formatValue(value, symbol)}
              </option>
            ))}
          </CFormSelect>
          {invalid && <CFormFeedback invalid>{formik.errors[field]}</CFormFeedback>}

          <div className="fv-preview">
            {added.length > 0 ? (
              <>
                Adds <strong>{added.length}</strong> value{added.length !== 1 ? 's' : ''}:{' '}
                {formatValue(added[0], symbol)}
                {added.length > 1 && <> to {formatValue(added[added.length - 1], symbol)}</>}
              </>
            ) : (
              <>
                Stops at the default <strong>{formatValue(edge, symbol)}</strong>
              </>
            )}
          </div>

          <div className="fv-card-actions">
            <CLoadingButton
              type="submit"
              color="primary"
              loading={loader}
              disabled={!formik.dirty || loader}
            >
              Save Limit
            </CLoadingButton>
          </div>
        </CForm>
      </CCardBody>
    </CCard>
  )
}

export default LimitCard
